import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './context/ThemeContext';
import MatrixBackground from './components/MatrixBackground';
import BubblesBackground from './components/BubblesBackground';
import TeacherLogin from './components/teacher/TeacherLogin';
import Dashboard from './components/teacher/Dashboard';
import QuizEditor from './components/teacher/QuizEditor';
import QuizMonitor from './components/teacher/QuizMonitor';
import QuizResults from './components/teacher/QuizResults';
import QuizPrint from './components/teacher/QuizPrint';
import GradeAnswers from './components/teacher/GradeAnswers';
import Settings from './components/teacher/Settings';
import TeacherProjects from './components/teacher/TeacherProjects';
import ProjectSubmissions from './components/teacher/ProjectSubmissions';
import QuestionBank from './components/teacher/QuestionBank';
import TeacherManagement from './components/teacher/TeacherManagement';
import Gradebooks from './components/teacher/Gradebooks';
import GradebookDetail from './components/teacher/GradebookDetail';
import Rubrics from './components/teacher/Rubrics';
import RubricEditor from './components/teacher/RubricEditor';
import RubricEvaluate from './components/teacher/RubricEvaluate';
import RubricPrint from './components/teacher/RubricPrint';
import StudentJoin from './components/student/StudentJoin';
import StudentLobby from './components/student/StudentLobby';
import StudentExam from './components/student/StudentExam';
import StudentResult from './components/student/StudentResult';
import StudentProject from './components/student/StudentProject';
import StudentGrades from './components/student/StudentGrades';
import StudentJoinGrade from './components/student/StudentJoinGrade';
import StudentLibrary from './components/student/StudentLibrary';
import StudentVideoWatch from './components/student/StudentVideoWatch';
import LibraryResources from './components/teacher/LibraryResources';
import LibraryVideos from './components/teacher/LibraryVideos';
import LessonPlans from './components/teacher/LessonPlans';
import LibraryVideoDetail from './components/teacher/LibraryVideoDetail';
import SubLogin from './components/substitution/SubLogin';
import SubDistribute from './components/substitution/SubDistribute';
import SubTeachers from './components/substitution/SubTeachers';
import SubStats from './components/substitution/SubStats';
import SubPrint from './components/substitution/SubPrint';
import SubStatsPrint from './components/substitution/SubStatsPrint';
import SubSettings from './components/substitution/SubSettings';
import SubSupervisionPrint from './components/substitution/SubSupervisionPrint';
import SubSwap from './components/substitution/SubSwap';
import SubSwapPrint from './components/substitution/SubSwapPrint';
import GradesLogin from './components/grades/GradesLogin';
import GradesAdmin from './components/grades/GradesAdmin';
import GradesTeachers from './components/grades/GradesTeachers';
import GradesStudents from './components/grades/GradesStudents';
import GradesEnter from './components/grades/GradesEnter';
import GradesStats from './components/grades/GradesStats';
import GradesParent from './components/grades/GradesParent';
import GradesFullMarksPrint from './components/grades/GradesFullMarksPrint';
import GradesSettings from './components/grades/GradesSettings';
import './App.css';

const SubRoute = ({ children }) => {
  const token = localStorage.getItem('subToken');
  return token ? children : <Navigate to="/substitution/login" replace />;
};

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('teacherToken');
  return token ? children : <Navigate to="/teacher/login" replace />;
};

const AdminRoute = ({ children }) => {
  const token = localStorage.getItem('teacherToken');
  if (!token) return <Navigate to="/teacher/login" replace />;
  if (localStorage.getItem('teacherRole') !== 'admin') return <Navigate to="/teacher/dashboard" replace />;
  return children;
};

const GradesRoute = ({ children }) => {
  const token = localStorage.getItem('gradesToken');
  return token ? children : <Navigate to="/grades/login" replace />;
};

const GradesAdminRoute = ({ children }) => {
  const token = localStorage.getItem('gradesToken');
  if (!token) return <Navigate to="/grades/login" replace />;
  if (localStorage.getItem('gradesRole') !== 'admin') return <Navigate to="/grades" replace />;
  return children;
};

const GradesHome = () => (localStorage.getItem('gradesRole') === 'admin' ? <GradesAdmin /> : <GradesEnter />);

function App() {
  return (
    <ThemeProvider>
      <div dir="rtl" className="font-tajawal">
        <MatrixBackground />
        <BubblesBackground />
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<StudentJoin />} />
            <Route path="/join/:urlCode" element={<StudentJoin />} />
            <Route path="/project/:code" element={<StudentProject />} />
            <Route path="/my-grades" element={<StudentGrades />} />
            <Route path="/g/:code" element={<StudentJoinGrade />} />
            <Route path="/library/:code" element={<StudentLibrary />} />
            <Route path="/b/:code" element={<StudentLibrary />} />
            <Route path="/watch/:code" element={<StudentVideoWatch />} />
            <Route path="/quiz/:quizId/lobby" element={<StudentLobby />} />
            <Route path="/quiz/:quizId/exam" element={<StudentExam />} />
            <Route path="/quiz/:quizId/result/:submissionId" element={<StudentResult />} />
            <Route path="/teacher/login" element={<TeacherLogin />} />
            <Route path="/teacher/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/teacher/projects" element={<ProtectedRoute><TeacherProjects /></ProtectedRoute>} />
            <Route path="/teacher/projects/:projectId/submissions" element={<ProtectedRoute><ProjectSubmissions /></ProtectedRoute>} />
            <Route path="/teacher/question-bank" element={<ProtectedRoute><QuestionBank /></ProtectedRoute>} />
            <Route path="/teacher/gradebooks" element={<ProtectedRoute><Gradebooks /></ProtectedRoute>} />
            <Route path="/teacher/gradebooks/:gradebookId" element={<ProtectedRoute><GradebookDetail /></ProtectedRoute>} />
            <Route path="/teacher/rubrics" element={<ProtectedRoute><Rubrics /></ProtectedRoute>} />
            <Route path="/teacher/rubrics/new" element={<ProtectedRoute><RubricEditor /></ProtectedRoute>} />
            <Route path="/teacher/rubrics/:rubricId/edit" element={<ProtectedRoute><RubricEditor /></ProtectedRoute>} />
            <Route path="/teacher/rubrics/:rubricId/evaluate" element={<ProtectedRoute><RubricEvaluate /></ProtectedRoute>} />
            <Route path="/teacher/rubrics/:rubricId/print" element={<ProtectedRoute><RubricPrint /></ProtectedRoute>} />
            <Route path="/teacher/library" element={<ProtectedRoute><LibraryResources /></ProtectedRoute>} />
            <Route path="/teacher/library/videos" element={<ProtectedRoute><LibraryVideos /></ProtectedRoute>} />
            <Route path="/teacher/library/plans" element={<ProtectedRoute><LessonPlans /></ProtectedRoute>} />
            <Route path="/teacher/library/videos/:videoId" element={<ProtectedRoute><LibraryVideoDetail /></ProtectedRoute>} />
            <Route path="/teacher/teachers" element={<AdminRoute><TeacherManagement /></AdminRoute>} />
            <Route path="/teacher/quiz/new" element={<ProtectedRoute><QuizEditor /></ProtectedRoute>} />
            <Route path="/teacher/quiz/:quizId/edit" element={<ProtectedRoute><QuizEditor /></ProtectedRoute>} />
            <Route path="/teacher/quiz/:quizId/monitor" element={<ProtectedRoute><QuizMonitor /></ProtectedRoute>} />
            <Route path="/teacher/quiz/:quizId/results" element={<ProtectedRoute><QuizResults /></ProtectedRoute>} />
            <Route path="/teacher/quiz/:quizId/print" element={<ProtectedRoute><QuizPrint /></ProtectedRoute>} />
            <Route path="/teacher/quiz/:quizId/grade" element={<ProtectedRoute><GradeAnswers /></ProtectedRoute>} />
            <Route path="/teacher/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/substitution/login" element={<SubLogin />} />
            <Route path="/substitution" element={<SubRoute><SubDistribute /></SubRoute>} />
            <Route path="/substitution/teachers" element={<SubRoute><SubTeachers /></SubRoute>} />
            <Route path="/substitution/stats" element={<SubRoute><SubStats /></SubRoute>} />
            <Route path="/substitution/settings" element={<SubRoute><SubSettings /></SubRoute>} />
            <Route path="/substitution/print/:date" element={<SubRoute><SubPrint /></SubRoute>} />
            <Route path="/substitution/swap" element={<SubRoute><SubSwap /></SubRoute>} />
            <Route path="/substitution/swap/print/:date" element={<SubRoute><SubSwapPrint /></SubRoute>} />
            <Route path="/substitution/stats/print" element={<SubRoute><SubStatsPrint /></SubRoute>} />
            <Route path="/substitution/supervision/print" element={<SubRoute><SubSupervisionPrint /></SubRoute>} />
            <Route path="/grades/login" element={<GradesLogin />} />
            <Route path="/grades/parent" element={<GradesParent />} />
            <Route path="/grades" element={<GradesRoute><GradesHome /></GradesRoute>} />
            <Route path="/grades/settings" element={<GradesAdminRoute><GradesSettings /></GradesAdminRoute>} />
            <Route path="/grades/teachers" element={<GradesAdminRoute><GradesTeachers /></GradesAdminRoute>} />
            <Route path="/grades/students" element={<GradesAdminRoute><GradesStudents /></GradesAdminRoute>} />
            <Route path="/grades/enter" element={<GradesRoute><GradesEnter /></GradesRoute>} />
            <Route path="/grades/stats" element={<GradesRoute><GradesStats /></GradesRoute>} />
            <Route path="/grades/full-marks/print" element={<GradesRoute><GradesFullMarksPrint /></GradesRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
